import React from "react";
import { useDashTheme } from "../../../hooks/useDashTheme";
import { ToolIcon } from "../../../components/classic/panels";
import { CATS, useCatColors, fmtDur, hourLabel } from "../bits";
import { Sk } from "./storyBits";

/* Hour by hour — the day cut into org-timezone hours, zero AI.
   ─────────────────────────────────────────────────────────────────────────
   Everything here derives from the /day payload the page already holds, so
   switching to this tab costs nothing. Each row answers exactly two
   questions — how full was the hour, and with what kind of work — via a
   battery-style fill bar; position-within-the-hour lives in the drawer's
   minute-by-minute, not here.

   All bucketing shifts instants by the server-resolved org offset
   (payload.tz.offsetMin) and reads UTC fields, so the browser's own zone is
   never consulted. */

const HOUR_MS = 3600 * 1000;

/** Split the day's working blocks across org-hour boundaries and roll each
 *  hour up. Returns rows sorted by hour, only hours that saw any work. */
export function bucketHours(data, offsetMin) {
  const blocks = (data?.blocks || []).filter((b) => b.cat !== "idle");
  if (!blocks.length) return [];
  const off = Number.isFinite(offsetMin) ? offsetMin : 0;

  const map = new Map();
  for (const b of blocks) {
    let s = b.startMs;
    while (s < b.endMs) {
      const shifted = s + off * 60000;
      const hourStart = shifted - (shifted % HOUR_MS) - off * 60000;
      const hEnd = hourStart + HOUR_MS;
      const e = Math.min(b.endMs, hEnd);
      const h = new Date(s + off * 60000).getUTCHours();
      if (!map.has(h)) map.set(h, { hour: h, startMs: hourStart, endMs: hEnd, slices: [] });
      const wallMin = Math.max(1 / 60, (b.endMs - b.startMs) / 60000);
      const share = (Number(b.minutes) || 0) / wallMin;
      map.get(h).slices.push({ ...b, startMs: s, endMs: e, minutes: ((e - s) / 60000) * share });
      s = e;
    }
  }

  return [...map.values()].sort((a, b) => a.hour - b.hour).map((H) => {
    const sl = H.slices.sort((a, b) => a.startMs - b.startMs);
    const trackedMin = Math.round(sl.reduce((a, x) => a + x.minutes, 0));
    const byCat = {};
    const byApp = {};
    let appMin = 0; let agentMin = 0;
    for (const x of sl) {
      byCat[x.cat] = (byCat[x.cat] || 0) + x.minutes;
      byApp[x.app] = byApp[x.app] || { minutes: 0, cat: x.cat };
      byApp[x.app].minutes += x.minutes;
      if (x.src === "agent") agentMin += x.minutes; else appMin += x.minutes;
    }
    // App changes, not slice count: a block split at the hour edge is the
    // same app continuing, and must not read as a switch.
    let changes = 0;
    for (let i = 1; i < sl.length; i += 1) if (sl[i].app !== sl[i - 1].app) changes += 1;
    const gaps = [];
    for (let i = 1; i < sl.length; i += 1) {
      const gapMs = sl[i].startMs - sl[i - 1].endMs;
      if (gapMs >= 3 * 60000) gaps.push(Math.round(gapMs / 60000));
    }
    const cats = Object.entries(byCat).map(([cat, minutes]) => ({ cat, minutes }))
      .sort((a, b) => b.minutes - a.minutes);
    const activity = Math.round(sl.reduce((a, x) => a + (x.activity || 0) * x.minutes, 0) / Math.max(trackedMin, 1));
    return {
      hour: H.hour,
      startMs: H.startMs,
      endMs: H.endMs,
      trackedMin,
      cats,
      domCat: cats[0]?.cat || "browse",
      apps: Object.entries(byApp).map(([name, v]) => ({ name, minutes: Math.round(v.minutes), cat: v.cat }))
        .sort((a, b) => b.minutes - a.minutes),
      activity,
      changes,
      gaps,
      source: agentMin && appMin ? "Both" : agentMin ? "Agent" : "Desktop",
      slices: sl,
    };
  });
}

/** The row's one-glance answer to "worth opening?". Deterministic. */
export function hourVerdict(h) {
  if (h.trackedMin < 8) return { level: "away", label: "Mostly away" };
  if (h.trackedMin < 20) return { level: "thin", label: "Thin" };
  if (h.changes >= 10) return { level: "warn", label: "Worth a look" };
  if (h.activity < 40) return { level: "warn", label: "Worth a look" };
  return { level: "ok", label: "Settled" };
}

function VChip({ v }) {
  const t = useDashTheme();
  const color = v.level === "ok" ? t.status.positive
    : v.level === "warn" ? t.status.warning
    : t.c.textTertiary;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
      fontSize: 11.5, fontWeight: 600, padding: "5px 11px", borderRadius: t.r.pill,
      color, background: `${color}${t.isDark ? "24" : "17"}`,
    }}>
      <i style={{ width: 6, height: 6, borderRadius: "50%", background: color }} />
      {v.label}
    </span>
  );
}

const COLS = "86px 1fr 118px 150px 122px 22px";

export function HourGrid({ data, loading, offsetMin, onOpenHour }) {
  const t = useDashTheme();
  const C = useCatColors();
  const hours = React.useMemo(() => bucketHours(data, offsetMin), [data, offsetMin]);

  if (loading) {
    return (
      <div style={{ padding: "6px 0" }}>
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: COLS, gap: 14, alignItems: "center", padding: "13px 20px" }}>
            <Sk w={54} h={15} /><Sk h={16} r={99} /><Sk w={60} h={26} r={8} /><Sk w={90} h={13} /><Sk w={84} h={22} r={99} /><span />
          </div>
        ))}
      </div>
    );
  }
  if (!hours.length) return null;

  const head = (txt) => (
    <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: t.c.textTertiary }}>{txt}</span>
  );

  return (
    <div>
      <div style={{
        display: "grid", gridTemplateColumns: COLS, gap: 14, alignItems: "center",
        padding: "10px 20px", borderBottom: `1px solid ${t.c.border}`,
        background: t.isDark ? t.c.surfaceMuted : t.c.surfaceMuted,
      }}>
        {head("Hour")}{head("Hour filled · of 60 min")}{head("Apps")}{head("Signals")}{head("Verdict")}<span />
      </div>

      {hours.map((h) => {
        const v = hourVerdict(h);
        const clickable = v.level !== "away";
        return (
          <div
            key={h.hour}
            className={clickable ? "tg-hgrid-row is-click" : "tg-hgrid-row"}
            role={clickable ? "button" : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={clickable && onOpenHour ? () => onOpenHour(h) : undefined}
            onKeyDown={clickable && onOpenHour ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpenHour(h); } } : undefined}
            style={{
              display: "grid", gridTemplateColumns: COLS, gap: 14, alignItems: "center",
              padding: "13px 20px", borderBottom: `1px solid ${t.c.border}`,
              opacity: clickable ? 1 : 0.6,
            }}
          >
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: "-0.01em", color: t.c.text }}>{hourLabel(h.hour)}</div>
              <div style={{ fontSize: 11.5, fontWeight: 600, color: t.c.textTertiary, marginTop: 2, fontVariantNumeric: "tabular-nums" }}>
                {h.trackedMin}m of 60
              </div>
            </div>

            {/* the battery: length = how much of the hour was worked,
                segments = the kinds of work, largest first */}
            <div style={{
              display: "flex", gap: 2.5, height: 16, borderRadius: 99, padding: 2.5,
              background: t.isDark ? t.c.surfaceHover : t.c.surfaceHover, alignItems: "stretch",
            }}>
              {h.cats.map((c) => (
                <span key={c.cat} style={{
                  flex: "none", minWidth: 5, borderRadius: 99,
                  width: `${Math.min(100, (c.minutes / 60) * 100)}%`,
                  background: C[c.cat] || C.browse,
                }} />
              ))}
            </div>

            <div style={{ display: "flex", alignItems: "center" }}>
              {h.apps.slice(0, 3).map((a, i) => (
                <span key={a.name} title={`${a.name} · ${fmtDur(a.minutes)}`} style={{
                  marginLeft: i ? -7 : 0, borderRadius: 9, lineHeight: 0,
                  boxShadow: `0 0 0 1.5px ${t.c.surface}`,
                }}>
                  <ToolIcon name={a.name} cat={a.cat} size={26} />
                </span>
              ))}
            </div>

            <div style={{ fontSize: 12.5, fontWeight: 600, color: t.c.textSecondary, lineHeight: 1.5, fontVariantNumeric: "tabular-nums" }}>
              {h.activity}% active
              <span style={{ display: "block", fontSize: 11, fontWeight: 500, color: t.c.textTertiary }}>
                {h.changes} switches · {h.source}
              </span>
            </div>

            <div><VChip v={v} /></div>
            <div style={{ color: t.c.textTertiary, fontSize: 15 }}>{clickable ? "›" : ""}</div>
          </div>
        );
      })}
    </div>
  );
}

/** Colour key + reading hint, under the grid. */
export function HourLegend() {
  const t = useDashTheme();
  const C = useCatColors();
  const item = (cat) => (
    <span key={cat} style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 11.5, fontWeight: 600, color: t.c.textSecondary }}>
      <i style={{ width: 9, height: 9, borderRadius: 3, background: C[cat] }} />
      {CATS[cat].label}
    </span>
  );
  return (
    <div style={{ display: "flex", gap: 18, alignItems: "center", flexWrap: "wrap", margin: "14px 4px 0" }}>
      {["deep", "review", "docs", "browse", "comms"].map(item)}
      <span style={{ fontSize: 11.5, fontWeight: 500, color: t.c.textTertiary }}>
        Bar = how much of the hour was worked, split by kind of work · grey = away
      </span>
    </div>
  );
}
